import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  GoogleFlightAirportTime,
  GoogleFlightCarbonEmissions,
  GoogleFlightCardData,
  GoogleFlightLeg,
  GoogleFlightSegment
} from './flightTypes';
import { Modal } from './Modal';

interface FlightCardProps {
  data: GoogleFlightCardData | GoogleFlightCardData[];
}

export const FlightCard: React.FC<FlightCardProps> = ({ data }) => {
  const { t } = useTranslation();
  const flights = (Array.isArray(data) ? data : [data]).map(normalizeFlightCardData);

  return (
    <div className="flight-cards-wrapper">
      <div className="cards-section-header">
        <h2 className="cards-section-title">{t('flightCard.sectionTitle')}</h2>
      </div>
      <div className="flight-cards-container">
        {flights.map((flight) => <FlightCardItem key={flight.id} flight={flight} />)}
      </div>
    </div>
  );
};

const FlightCardItem: React.FC<{ flight: GoogleFlightCardData }> = ({ flight }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const outbound = flight.legs[0];
  const returnLeg = flight.legs.find((leg) => leg.label === 'Return');
  const firstSegment = outbound?.segments[0];
  const lastSegment = outbound?.segments[outbound.segments.length - 1];
  const from = firstSegment?.departureAirport?.id || '?';
  const to = lastSegment?.arrivalAirport?.id || '?';
  const openModal = () => setIsModalOpen(true);

  return (
    <div className="flight-card-wrapper">
      <div
        className="flight-card"
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') openModal();
        }}
        role="button"
        tabIndex={0}
      >
        <div className="flight-card-header">
          <div className="flight-airline-summary">
            {firstSegment?.airlineLogo && <img src={firstSegment.airlineLogo} alt="" />}
            <div>
              <strong>{firstSegment?.airline || t('flightCard.airlineUnavailable')}</strong>
              {firstSegment?.flightNumber && <span>{firstSegment.flightNumber}</span>}
            </div>
          </div>
          <div className="flight-price">
            <span className="price-amount">{flight.price ?? t('flightCard.priceUnavailable')}</span>
            {flight.price !== undefined && <span className="price-currency">{flight.currency}</span>}
          </div>
        </div>

        <div className="flight-card-legs">
          <FlightLegSummary leg={outbound} label={t('flightCard.outboundFlight')} t={t} />
          {returnLeg && <FlightLegSummary leg={returnLeg} label={t('flightCard.returnFlight')} t={t} />}
        </div>

        <div className="flight-card-footer">
          <span className="flight-cabin">{firstSegment?.travelClass || flight.tripType}</span>
          <span>{flight.tripType}</span>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={`${from} → ${to}`}>
        <div className="flight-modal-content">
          <section className="details-section">
            <h3>{t('flightCard.priceBreakdown')}</h3>
            <div className="detail-row highlight">
              <span className="detail-label">{t('flightCard.flightPrice')}</span>
              <span className="detail-value">{flight.price !== undefined ? `${flight.price} ${flight.currency}` : '—'}</span>
            </div>
            <p>{t('flightCard.priceDisclaimer')}</p>
          </section>

          {flight.legs.map((leg) => (
            <section className="details-section" key={leg.label}>
              <h3>{leg.label === 'Return' ? t('flightCard.returnFlight') : t('flightCard.outboundFlight')}</h3>
              <p>{formatMinutes(leg.totalDuration)} · {formatStops(leg, t)}</p>
              {leg.segments.map((segment, index) => (
                <div className="segment-card" key={`${segment.flightNumber || 'segment'}-${index}`}>
                  <div className="segment-header">
                    <div className="segment-airline">
                      <strong>{segment.airline || t('flightCard.airlineUnavailable')} {segment.flightNumber || ''}</strong>
                      {segment.airplane && <span className="aircraft-code"> · {segment.airplane}</span>}
                    </div>
                  </div>
                  <div className="segment-route">
                    <Endpoint code={segment.departureAirport?.id} name={segment.departureAirport?.name} time={segment.departureAirport?.time} />
                    <div className="segment-arrow">
                      <div className="segment-duration">{formatMinutes(segment.duration)}</div>
                      <span>→</span>
                    </div>
                    <Endpoint code={segment.arrivalAirport?.id} name={segment.arrivalAirport?.name} time={segment.arrivalAirport?.time} />
                  </div>
                  {(segment.travelClass || segment.legroom || segment.overnight) && (
                    <p>{[segment.travelClass, segment.legroom, segment.overnight ? t('flightCard.overnight') : undefined].filter(Boolean).join(' · ')}</p>
                  )}
                </div>
              ))}
              {leg.layovers.map((layover, index) => (
                <p className="layover-indicator" key={`${layover.id || layover.name}-${index}`}>
                  {t('flightCard.layoverAt', { airport: layover.name || layover.id || '?' })} · {formatMinutes(layover.duration)}
                  {layover.overnight ? ` · ${t('flightCard.overnight')}` : ''}
                </p>
              ))}
            </section>
          ))}

          {flight.carbonEmissions?.differencePercent !== undefined && (
            <section className="details-section">
              <h3>{t('flightCard.emissions')}</h3>
              <p>{t('flightCard.emissionsDifference', { difference: flight.carbonEmissions.differencePercent })}</p>
            </section>
          )}
        </div>
      </Modal>
    </div>
  );
};

const Endpoint: React.FC<{ code?: string; name?: string; time?: string }> = ({ code, name, time }) => (
  <div className="segment-endpoint">
    <div className="endpoint-code">{code || '?'}</div>
    {name && <div>{name}</div>}
    <div className="endpoint-time">{time || '—'}</div>
  </div>
);

const FlightLegSummary: React.FC<{
  leg: GoogleFlightLeg;
  label: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ leg, label, t }) => {
  const firstSegment = leg.segments[0];
  const lastSegment = leg.segments[leg.segments.length - 1];
  const departure = splitFlightDateTime(firstSegment?.departureAirport?.time);
  const arrival = splitFlightDateTime(lastSegment?.arrivalAirport?.time);

  return (
    <div className="flight-leg-summary">
      <div className="flight-leg-heading">
        <span>{label}</span>
        <strong>{departure.date}</strong>
      </div>
      <div className="flight-leg-route">
        <div>
          <strong>{firstSegment?.departureAirport?.id || '?'}</strong>
          <span>{departure.time}</span>
        </div>
        <div className="flight-leg-line">
          <span>{formatMinutes(leg.totalDuration)}</span>
          <i>→</i>
          <span>{formatStops(leg, t)}</span>
        </div>
        <div>
          <strong>{lastSegment?.arrivalAirport?.id || '?'}</strong>
          <span>{arrival.time}</span>
        </div>
      </div>
    </div>
  );
};

function formatMinutes(minutes?: number): string {
  if (minutes === undefined) return '—';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}h ${remainder}m`;
}

function formatStops(leg: GoogleFlightLeg | undefined, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (!leg) return '—';
  const stops = Math.max(0, leg.segments.length - 1);
  return stops === 0 ? t('flightCard.nonstop') : t('flightCard.stops', { count: stops });
}

function splitFlightDateTime(value?: string): { date: string; time: string } {
  if (!value) return { date: '—', time: '—' };
  const [date = '', time = ''] = value.split(' ');
  const [year, month, day] = date.split('-');
  return {
    date: year && month && day ? `${day}/${month}/${year}` : date,
    time: time || '—'
  };
}

type LegacyFlightSegment = GoogleFlightSegment & {
  departure_airport?: GoogleFlightAirportTime;
  arrival_airport?: GoogleFlightAirportTime;
  airline_logo?: string;
  travel_class?: string;
  flight_number?: string;
  often_delayed_by_over_30_min?: boolean;
};

type LegacyCarbonEmissions = GoogleFlightCarbonEmissions & {
  this_flight?: number;
  typical_for_this_route?: number;
  difference_percent?: number;
};

type LegacyFlightCardData = GoogleFlightCardData & {
  carbon_emissions?: LegacyCarbonEmissions;
};

function normalizeFlightCardData(flight: GoogleFlightCardData): GoogleFlightCardData {
  const legacyFlight = flight as LegacyFlightCardData;
  const rawEmissions = (flight.carbonEmissions ?? legacyFlight.carbon_emissions) as LegacyCarbonEmissions | undefined;

  return {
    ...flight,
    legs: flight.legs.map((leg) => ({
      ...leg,
      segments: leg.segments.map((segment) => {
        const legacySegment = segment as LegacyFlightSegment;
        return {
          ...segment,
          departureAirport: segment.departureAirport ?? legacySegment.departure_airport,
          arrivalAirport: segment.arrivalAirport ?? legacySegment.arrival_airport,
          airlineLogo: segment.airlineLogo ?? legacySegment.airline_logo,
          travelClass: segment.travelClass ?? legacySegment.travel_class,
          flightNumber: segment.flightNumber ?? legacySegment.flight_number,
          oftenDelayedByOverThirtyMinutes:
            segment.oftenDelayedByOverThirtyMinutes ?? legacySegment.often_delayed_by_over_30_min
        };
      })
    })),
    carbonEmissions: rawEmissions ? {
      thisFlight: rawEmissions.thisFlight ?? rawEmissions.this_flight,
      typicalForThisRoute: rawEmissions.typicalForThisRoute ?? rawEmissions.typical_for_this_route,
      differencePercent: rawEmissions.differencePercent ?? rawEmissions.difference_percent
    } : undefined
  };
}
